/**
 * Document Field Utilities
 * 
 * This file contains utility functions for handling document fields in the workflow system.
 * It provides consistent methods for:
 * - Detecting document fields
 * - Extracting document URLs
 * - Normalizing document field structures
 * - Handling document viewing and downloading
 */

/**
 * Check if a field is a document field based on its name, type, or structure
 * @param {Object} field - The field to check
 * @returns {boolean} - True if the field is a document field
 */
export const isDocumentField = (field) => {
  if (!field) return false;
  
  // Check by field type
  if (field.type && typeof field.type === 'string') {
    const type = field.type.toLowerCase();
    if (type === 'document' || type === 'pdf' || type === 'image' || type === 'file') {
      return true;
    }
    // Explicit scalar types are never document fields, regardless of the field name — e.g.
    // "passportNumber" / "visaNumber" are TEXT/NUMBER fields, not the passport/visa copy.
    if (['text', 'number', 'date', 'textarea', 'email', 'phone', 'select', 'checkbox', 'boolean', 'string', 'time'].includes(type)) {
      return false;
    }
  }
  
  // Check by field structure (target pattern)
  if (field.value && field.value.target && 
      field.value.target.type && 
      field.value.target.type.toLowerCase() === 'document') {
    return true;
  }
  
  // Check by field structure (hasFile pattern)
  if (field.value && typeof field.value === 'object' && 
      'hasFile' in field.value && 
      'uploadStatus' in field.value) {
    return true;
  }
  
  // Check by field name (only reached when the type didn't decide)
  if (field.name && typeof field.name === 'string') {
    const name = field.name.toLowerCase();
    // "...Number" fields (passportNumber, visaNumber, emiratesIdNumber) are text, not documents.
    if (name.includes('number')) {
      return false;
    }
    const documentFieldNames = [
      'document', 'visa', 'passport', 'certificate', 'photo', 
      'visapage', 'passportcopy', 'emiratesid', 'birthcertificate',
      'laborcontract', 'offerletter'
    ];
    
    if (documentFieldNames.some(docName => name.includes(docName))) {
      return true;
    }
  }
  
  // Check if value is a string that looks like a document reference
  if (field.value && typeof field.value === 'string') {
    const value = field.value.toLowerCase();
    if ((value.includes('documenturl') || 
         value.includes('documenturi') || 
         value.includes('documenttype')) && 
        (value.startsWith('{') && value.endsWith('}'))) {
      return true;
    }
  }
  
  return false;
};

/**
 * Extract document URL from a field value
 * @param {Object|string} fieldValue - The field value to extract URL from
 * @returns {string|null} - The extracted document URL or null if not found
 */
export const extractDocumentUrl = (fieldValue) => {
  if (!fieldValue) return null;
  
  // Handle nested target structure
  if (typeof fieldValue === 'object' && fieldValue.target) {
    const target = fieldValue.target;
    if (target.value && target.value.documentUrl) {
      return target.value.documentUrl;
    }
  }
  
  // Handle direct object with documentUrl
  if (typeof fieldValue === 'object' && fieldValue.documentUrl) {
    return fieldValue.documentUrl;
  }
  
  // Handle string value
  if (typeof fieldValue === 'string') {
    // Try to extract documentUrl or documentUri
    const urlMatch = fieldValue.match(/documentUrl=([^,}\]]+)/i) || 
                    fieldValue.match(/documentUri=([^,}\]]+)/i) ||
                    fieldValue.match(/documentUri":"([^"]+)/i);
    
    if (urlMatch && urlMatch[1]) {
      let url = urlMatch[1];
      
      // Remove any trailing special characters that might cause issues
      url = url.replace(/[}\]\)>]+$/, '');
      
      // If the URL doesn't start with http or /, assume it's a relative path
      if (!url.startsWith('http') && !url.startsWith('/')) {
        url = '/' + url;
      }
      
      return url;
    }
    
    // If we couldn't extract using regex, try a different approach
    if (fieldValue.includes('/documents/') || fieldValue.includes('/documentUri=')) {
      const parts = fieldValue.split(/documentUri=|documentUrl=/i);
      if (parts.length > 1) {
        let url = parts[1].split(/[,}\]\)>"]/)[0];
        
        // If the URL doesn't start with http or /, assume it's a relative path
        if (!url.startsWith('http') && !url.startsWith('/')) {
          url = '/' + url;
        }
        
        return url;
      }
    }
  }
  
  return null;
};

/**
 * Extract file name from a document field value
 * @param {Object|string} fieldValue - The field value to extract file name from
 * @returns {string} - The extracted file name or "Document" if not found
 */
export const extractFileName = (fieldValue) => {
  if (!fieldValue) return "Document";
  
  // Handle nested target structure
  if (typeof fieldValue === 'object' && fieldValue.target) {
    const target = fieldValue.target;
    if (target.value) {
      if (target.value.originalName) return target.value.originalName;
      if (target.value.fileName) return target.value.fileName;
    }
  }
  
  // Handle direct object with fileName
  if (typeof fieldValue === 'object') {
    if (fieldValue.originalName) return fieldValue.originalName;
    if (fieldValue.fileName) return fieldValue.fileName;
  }
  
  // Handle string value
  if (typeof fieldValue === 'string') {
    // Try to extract fileName
    const fileNameMatch = fieldValue.match(/fileName=([^,}\]]+)/i) ||
                         fieldValue.match(/originalName=([^,}\]]+)/i);
    
    if (fileNameMatch && fileNameMatch[1]) {
      return fileNameMatch[1].replace(/[}\]\)>]+$/, '');
    }
  }
  
  return "Document";
};

/**
 * Extract user/employee ID from a document field value
 * @param {Object|string} fieldValue - The field value to extract ID from
 * @returns {string|null} - The extracted ID or null if not found
 */
export const extractUserId = (fieldValue) => {
  if (!fieldValue) return null;
  
  // Handle nested target structure
  if (typeof fieldValue === 'object' && fieldValue.target) {
    const target = fieldValue.target;
    if (target.value && target.value.uid) {
      return target.value.uid;
    }
  }
  
  // Handle direct object with uid
  if (typeof fieldValue === 'object' && fieldValue.uid) {
    return fieldValue.uid;
  }
  
  // Handle string value
  if (typeof fieldValue === 'string') {
    // Try to extract uid or employeeUid
    const uidMatch = fieldValue.match(/employeeUid=([^,}\]]+)/i) || 
                    fieldValue.match(/uid=([^,}\]]+)/i);
    
    if (uidMatch && uidMatch[1]) {
      return uidMatch[1].replace(/[}\]\)>]+$/, '');
    }
  }
  
  return null;
};

/**
 * Extract document type from a document field value
 * @param {Object|string} fieldValue - The field value to extract document type from
 * @param {string} fieldName - The field name to use as fallback
 * @returns {string|null} - The extracted document type or null if not found
 */
export const extractDocumentType = (fieldValue, fieldName) => {
  if (!fieldValue) return fieldName || null;
  
  // Handle nested target structure
  if (typeof fieldValue === 'object' && fieldValue.target) {
    const target = fieldValue.target;
    if (target.value && target.value.documentType) {
      return target.value.documentType;
    }
    if (target.name) {
      return target.name;
    }
  }
  
  // Handle direct object with documentType
  if (typeof fieldValue === 'object' && fieldValue.documentType) {
    return fieldValue.documentType;
  }
  
  // Handle string value
  if (typeof fieldValue === 'string') {
    // Try to extract documentType
    const typeMatch = fieldValue.match(/documentType=([^,}\]]+)/i);
    
    if (typeMatch && typeMatch[1]) {
      return typeMatch[1].replace(/[}\]\)>]+$/, '');
    }
  }
  
  return fieldName || null;
};

/**
 * Normalize a document field to ensure consistent structure
 * @param {Object} field - The field to normalize
 * @returns {Object} - The normalized field
 */
export const normalizeDocumentField = (field) => {
  if (!field) return field;
  
  const normalizedField = { ...field };
  
  // If the field already has the target structure, return it
  if (field.value && 
      typeof field.value === 'object' && 
      field.value.target && 
      field.value.target.value) {
    // Ensure the field type is set to 'document' for proper rendering
    normalizedField.type = 'document';
    return normalizedField;
  }
  
  // Handle the case where field.value is already a document object with hasFile and target
  if (field.value && 
      typeof field.value === 'object' && 
      field.value.hasFile !== undefined && 
      field.value.target) {
    // Ensure the field type is set to 'document' for proper rendering
    normalizedField.type = 'document';
    return normalizedField;
  }
  
  // Extract metadata from the field value
  const documentUrl = extractDocumentUrl(field.value);
  const fileName = extractFileName(field.value);
  const userId = extractUserId(field.value);
  const documentType = extractDocumentType(field.value, field.name);
  
  // Create the normalized structure
  normalizedField.value = {
    hasFile: !!documentUrl,
    uploadStatus: documentUrl ? 'UPLOADED' : 'PENDING',
    target: {
      name: field.name,
      type: 'DOCUMENT',
      value: {
        originalName: fileName,
        fileName: fileName,
        size: 0, // We don't have this information
        contentType: 'application/pdf', // Default
        documentUrl: documentUrl,
        uid: userId,
        documentType: documentType
      }
    }
  };
  
  // Set the field type to 'document' for proper rendering
  normalizedField.type = 'document';
  
  return normalizedField;
};

/**
 * Generate a document URL from field metadata
 * @param {Object} field - The document field
 * @returns {string|null} - The generated document URL or null if not possible
 */
export const generateDocumentUrl = (field) => {
  if (!field) return null;
  
  // Try to extract from normalized field
  const documentUrl = extractDocumentUrl(field.value);
  if (documentUrl) return documentUrl;
  
  // Try to generate from metadata
  const userId = extractUserId(field.value);
  const documentType = extractDocumentType(field.value, field.name);
  
  // Check if this is a dynamic service field by checking if field has filePath in target structure
  // Dynamic service fields have nested target.value.filePath structure
  const hasFilePath = field.value?.target?.value?.filePath || field.value?.filePath;
  const isDynamicServiceField = !!hasFilePath;
  
  if (isDynamicServiceField) {
    console.log('Dynamic service field detected by field name in documentUtils.generateDocumentUrl:', field.name);
    // For dynamic service fields, try to extract file path from field value
    // Check multiple possible locations for filePath
    const filePath = field.value?.filePath || 
                    field.value?.target?.value?.filePath || 
                    field.value?.documentUrl || 
                    field.value?.target?.value?.documentUrl;
    return filePath ? `/v1/files/download?filePath=${encodeURIComponent(filePath)}` : null;
  }
  
  // Also handle case where multiple documentReferences exist (possibly as string)
  const rawRefs = field.value?.documentReferences || field.value?.documentReferenceList || 
                  field.value?.target?.value?.documentReferences || field.value?.target?.value?.documentReferenceList;
  if (rawRefs) {
    const refs = parseDocumentReferences(rawRefs);
    if (refs.length > 0 && (refs[0].filePath || refs[0].path)) {
      const fp = refs[0].filePath || refs[0].path;
      return `/v1/files/download?filePath=${encodeURIComponent(fp)}`;
    }
  }
  
  if (userId && documentType) {
    return `/v1/users/${userId}/documents/${documentType}/download`;
  }
  
  return null;
};

/**
 * Create a document field renderer props object
 * @param {Object} field - The document field
 * @returns {Object} - Props for rendering the document field
 */
// Extract the `filePath` query param from a download URL (e.g. /v1/files/download?filePath=...).
const extractFilePathParam = (u) => {
  if (!u || typeof u !== 'string') return null;
  const m = /[?&]filePath=([^&]+)/.exec(u);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch (e) {
    return m[1];
  }
};

export const createDocumentFieldProps = (field) => {
  if (!field) return {};
  
  console.log('Creating document field props for:', field.name, field);
  
  // Handle case where field is already properly structured
  if (field.value && typeof field.value === 'object') {
    // Case 1: Field has target.value structure with documentUrl
    if (field.value.target && field.value.target.value) {
      const targetValue = field.value.target.value;
      const rawRefs = targetValue.documentReferences || targetValue.documentReferenceList;
      let refs = rawRefs ? parseDocumentReferences(rawRefs) : [];
      // Treat a valid-looking documentUrl as evidence even when hasFile=false
      const isLikelyValidUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        return u.startsWith('http') || u.startsWith('/v1/') || u.startsWith('/api/') || u.includes('/files/download');
      };
      // Directly-uploaded (dynamic) document fields store the file as s3Key / ticketDocumentPath /
      // downloadUrl instead of documentReferences. Synthesize a single reference from those so they
      // render and download like other documents.
      if (!refs || refs.length === 0) {
        const fp = targetValue.filePath || extractFilePathParam(targetValue.downloadUrl)
          || targetValue.s3Key || targetValue.ticketDocumentPath || null;
        if (fp) {
          refs = [{ filePath: fp, fileName: targetValue.originalName || targetValue.fileName || 'Document', isPrimary: true }];
        }
      }
      const backendHasFile = field.value?.hasFile;
      let hasFileStrict = Boolean(
        isLikelyValidUrl(targetValue.documentUrl) ||
        isLikelyValidUrl(targetValue.downloadUrl) ||
        isLikelyValidUrl(targetValue.s3Url) ||
        !!targetValue.filePath ||
        (Array.isArray(refs) && refs.length > 0)
      );
      console.debug('[DocUtils] createDocumentFieldProps Case1', {
        name: field.name,
        backendHasFile,
        hasFileStrict,
        hasDocUrl: !!targetValue.documentUrl,
        hasFilePath: !!targetValue.filePath,
        refsCount: Array.isArray(refs) ? refs.length : 0,
        targetValue
      });
      return {
        hasFile: hasFileStrict,
        hasFileStrict,
        fileName: (refs && refs.length > 0 && (refs.find(r => r.isPrimary)?.fileName || refs[0].fileName)) || targetValue.fileName || targetValue.originalName || '',
        documentUrl: targetValue.documentUrl || targetValue.downloadUrl || targetValue.s3Url || generateDocumentUrl(field),
        refs,
        field: field
      };
    }
    
    // Case 2: Field has direct value with documentUrl
    if (field.value.documentUrl) {
      const backendHasFile = field.value?.hasFile;
      const rawRefs = field.value.documentReferences || field.value.documentReferenceList;
      const refs = rawRefs ? parseDocumentReferences(rawRefs) : [];
      const isLikelyValidUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        return u.startsWith('http') || u.startsWith('/v1/') || u.startsWith('/api/') || u.includes('/files/download');
      };
      const hasFileStrict = Boolean(
        isLikelyValidUrl(field.value.documentUrl) ||
        !!field.value.filePath ||
        (Array.isArray(refs) && refs.length > 0)
      );
      console.debug('[DocUtils] createDocumentFieldProps Case2', {
        name: field.name,
        backendHasFile,
        hasFileStrict,
        hasDocUrl: !!field.value.documentUrl,
        hasFilePath: !!field.value.filePath,
        refsCount: Array.isArray(refs) ? refs.length : 0
      });
      return {
        hasFile: hasFileStrict,
        hasFileStrict,
        fileName: field.value.fileName || field.value.originalName || '',
        documentUrl: field.value.documentUrl,
        refs,
        field: field
      };
    }
    
    // Case 3: Field is a direct document structure (birthCertificate, passportCopy, etc.)
    // This is the case where the field itself is the document structure
    if (field.value.hasFile !== undefined && field.value.target) {
      const targetValue = field.value.target.value;
      const rawRefs = targetValue?.documentReferences || targetValue?.documentReferenceList;
      const refs = rawRefs ? parseDocumentReferences(rawRefs) : [];
      const backendHasFile = field.value?.hasFile;
      const isLikelyValidUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        return u.startsWith('http') || u.startsWith('/v1/') || u.startsWith('/api/') || u.includes('/files/download');
      };
      const hasFileStrict = Boolean(
        isLikelyValidUrl(targetValue?.documentUrl) ||
        !!targetValue?.filePath ||
        (Array.isArray(refs) && refs.length > 0)
      );
      console.debug('[DocUtils] createDocumentFieldProps Case3', {
        name: field.name,
        backendHasFile,
        hasFileStrict,
        hasDocUrl: !!targetValue?.documentUrl,
        hasFilePath: !!targetValue?.filePath,
        refsCount: Array.isArray(refs) ? refs.length : 0,
        targetValue
      });
      return {
        hasFile: hasFileStrict,
        hasFileStrict,
        fileName: (refs && refs.length > 0 && (refs.find(r => r.isPrimary)?.fileName || refs[0].fileName)) || targetValue?.fileName || targetValue?.originalName || '',
        documentUrl: targetValue?.documentUrl || generateDocumentUrl(field),
        refs,
        field: field
      };
    }
  }
  
  // Fallback to normalization for other cases
  const normalizedField = normalizeDocumentField(field);
  const documentUrl = generateDocumentUrl(normalizedField);
  const fileName = extractFileName(normalizedField.value);
  // Compute strict only from concrete evidence in normalized value
  const targetValue = normalizedField?.value?.target?.value || {};
  const rawRefs = targetValue.documentReferences || targetValue.documentReferenceList;
  const refs = rawRefs ? parseDocumentReferences(rawRefs) : [];
  const isLikelyValidUrl = (u) => {
    if (!u || typeof u !== 'string') return false;
    return u.startsWith('http') || u.startsWith('/v1/') || u.startsWith('/api/') || u.includes('/files/download');
  };
  const hasFileStrict = Boolean(
    isLikelyValidUrl(documentUrl) ||
    !!targetValue.filePath || (Array.isArray(refs) && refs.length > 0)
  );
  console.debug('[DocUtils] createDocumentFieldProps Fallback', {
    name: field.name,
    hasFileNormalized: normalizedField.value && normalizedField.value.hasFile,
    hasFileStrict,
    hasDocUrl: !!documentUrl,
    hasFilePath: !!targetValue.filePath,
    refsCount: Array.isArray(refs) ? refs.length : 0
  });
  return {
    hasFile: hasFileStrict,
    hasFileStrict,
    fileName: fileName,
    documentUrl: documentUrl,
    refs,
    field: normalizedField
  };
};

/**
 * Parse documentReferences which can be array or Java Map-style string
 * @param {any} raw
 * @returns {Array<{fileName?: string, filePath?: string, path?: string, isPrimary?: boolean}>}
 */
export const parseDocumentReferences = (raw) => {
  try {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      const entries = [];
      const regex = /\{([^}]*)\}/g;
      let match;
      while ((match = regex.exec(raw)) !== null) {
        const objStr = match[1];
        const parts = objStr.split(/,\s+/);
        const obj = {};
        parts.forEach(p => {
          const eqIdx = p.indexOf('=');
          if (eqIdx > -1) {
            const key = p.substring(0, eqIdx).trim();
            const valueRaw = p.substring(eqIdx + 1).trim();
            if (valueRaw === 'true') obj[key] = true;
            else if (valueRaw === 'false') obj[key] = false;
            else obj[key] = valueRaw;
          }
        });
        entries.push(obj);
      }
      return entries;
    }
    return [];
  } catch (e) {
    console.warn('parseDocumentReferences failed', e, raw);
    return [];
  }
};
